/**
 * Simulation constants. Everything here is in "world units" unless noted.
 * The camera and the physics share one unit system so tuning one does not
 * silently break the other.
 */

/** Length of one track segment. The whole track is a list of these. */
export const SEGMENT_LENGTH = 200;

/**
 * Half-width of the rideable surface. Player x is normalised to [-1, 1] of this.
 * Narrow enough that the trail reads as singletrack with terrain either side.
 */
export const ROAD_HALF_WIDTH = 850;

export const LANE_COUNT = 3;

/** Normalised x of each lane centre. Outer lanes sit inside the edge so the tyre never clips. */
export const LANE_X = [-0.62, 0, 0.62] as const;

/** Player collision half-width in normalised road units. */
export const PLAYER_HALF_W = 0.13;

/** Fixed simulation step. Every mode, ghost and race agrees on this. */
export const TICK_HZ = 60;
export const TICK_MS = 1000 / TICK_HZ;
export const TICK_S = 1 / TICK_HZ;

/** Base cruising ceiling, units/second, before theme and flow multipliers. */
export const BASE_MAX_SPEED = 8800;
export const MIN_SPEED = 1400;

/** How hard gravity pulls you down the hill into speed. */
export const ACCEL = 5200;
/** Coasting drag, applied proportional to speed². */
export const DRAG = 0.9;
/** Extra drag when tucking is released and you sit upright. */
export const UPRIGHT_DRAG = 1.35;
/** Tuck reduces drag and raises the ceiling. */
export const TUCK_DRAG = 0.42;
export const TUCK_SPEED_BONUS = 1.16;

/** Off the edge of the trail: heavy but survivable. */
export const OFFROAD_DECEL = 9000;
export const OFFROAD_MAX_SPEED = 4200;

/** Lateral steering rate in normalised units/second at full stick. */
export const STEER_RATE = 2.35;
/** Steering loses authority as you slow to a crawl, and at very high speed. */
export const STEER_LOW_SPEED_FLOOR = 0.35;

/**
 * Centrifugal push out of a corner. Deliberately weaker than lane magnetism so
 * a parked thumb holds its line through a bend — you feel the pull, you do not
 * get evicted by it.
 */
export const CENTRIFUGAL = 0.075;

export const GRAVITY = 5600;
export const HOP_VELOCITY = 1750;
/** Holding the hop input during the first moments of a jump adds lift. */
export const HOP_HOLD_BOOST = 3200;
export const HOP_HOLD_MAX_S = 0.18;
export const RAMP_VELOCITY = 2450;

/** Flow meter: fills from orbs, near misses and tricks; spends on SEND. */
export const FLOW_MAX = 100;
export const FLOW_SEND_COST = 34;
export const FLOW_SEND_MS = 2200;
export const SEND_SPEED_MUL = 1.42;
export const SEND_INVULN = false;

/** Crash recovery. */
export const CRASH_STUN_MS = 900;
export const CRASH_INVULN_MS = 1500;
export const CRASH_SPEED_KEEP = 0.32;
export const MAX_CRASHES_ENDLESS = 3;

/** Scoring. */
export const SCORE_PER_METRE = 24;
export const SCORE_ORB = 60;
export const SCORE_GEAR = 900;
export const SCORE_NEAR_MISS = 45;
export const SCORE_AIR_PER_S = 260;
export const SCORE_TRICK_BASE = 180;
export const COMBO_WINDOW_MS = 2600;
export const COMBO_MAX = 12;

/** World units per displayed metre. Puts a tucked descent at a believable ~52 km/h. */
export const UNITS_PER_METRE = 700;

/**
 * Presses are remembered for this long, so hitting hop a moment before you land
 * still works. Without it a held button eats its own edge and nothing fires.
 */
export const INPUT_BUFFER_MS = 260;

/**
 * Camera. Height and set-back are chosen together so the rider sits low in the
 * frame with little foreground below him, looking down onto the trail.
 */
export const CAMERA_HEIGHT = 1375;
export const CAMERA_BACK = 1720;
/**
 * Runs begin here rather than at zero so the camera, which sits CAMERA_BACK
 * behind the rider, always has track in front of it.
 */
export const START_Z = CAMERA_BACK;
export const CAMERA_DEPTH_FOV = 96;
export const DRAW_DISTANCE = 200;
/** Segments of extra look-ahead used only for hazard warning UI. */
export const WARN_DISTANCE = 40;

/** Track generation. */
export const RUMBLE_LENGTH = 4;
/** Minimum segments between obstacle rows at the lowest intensity. */
export const ROW_GAP_MAX = 46;
/** ...and at the highest. Reaction time floor is enforced on top of this. */
export const ROW_GAP_MIN = 15;
/** No obstacles at all for this many segments after the start gate. */
export const SAFE_START_SEGMENTS = 90;
/** Rows must always leave at least this many lanes clear. */
export const MIN_CLEAR_LANES = 1;
/** Hard floor on reaction time, seconds, between consecutive rows. */
export const MIN_REACTION_S = 0.72;

/** Trail lengths, in segments. */
export const TRAIL_LENGTH = 2600;

/** Ghost recording: sample rate. 10 Hz keeps a 3-minute run under ~30 KB. */
export const GHOST_HZ = 10;
export const GHOST_MAX_FRAMES = 2400;

/** Bump this when physics or scoring changes so old boards can be retired. */
export const RULES_VERSION = 1;
